import { UITexts } from '../../types/ui';

export const uiTexts: UITexts = {
  language: {
    name: "English",
    nativeName: "English",
    code: "en",
    flag: "🇺🇸"
  },

  common: {
    loading: "Loading...",
    error: "Error",
    retry: "Retry",
    back: "Back",
    next: "Next",
    previous: "Previous",
    finish: "Finish",
    start: "Start",
    restart: "Restart",
    continue: "Continue",
    cancel: "Cancel",
    close: "Close",
    ok: "OK",
    yes: "Yes",
    no: "No",
    save: "Save",
    share: "Share",
    copy: "Copy",
    copied: "Copied!",
    minutes: "minutes",
    seconds: "seconds",
    question: "question",
    questions: "questions",
    answer: "answer",
    answers: "answers",
    correct: "Correct",
    incorrect: "Incorrect",
    score: "Score",
    time: "Time",
    difficulty: {
      easy: "Easy",
      medium: "Medium",
      hard: "Hard",
      // ISTQB Knowledge levels
      k1: "K1",
      k2: "K2",
      k3: "K3",
      k4: "K4"
    }
  },

  demo: {
    title: "AI Tester",
    subtitle: "Demo Challenge",
    description: "Ready for an AI tester salary? Find out in 15 minutes",
    startButton: "Start Free Demo",
    badge: "FREE DEMO VERSION",
    features: {
      title: "What You'll Experience in Full Version",
      items: [
        "300+ Expert-Level Questions",
        "11 Complete Syllabus Chapters",
        "40-minute Timed Exam Simulation",
        "Detailed Answer Explanations",
        "Performance Analytics & Scoring",
        "Multiple Practice Sessions",
        "Instant & Lifetime Access",
        "Mobile-Friendly Format",
        "Bonus: Early Access to Advanced AI Testing Course"
      ],
      smartQuestions: {
        title: "Insightful Questions",
        description: "Real-world scenarios that test practical AI testing knowledge"
      },
      instantFeedback: {
        title: "Instant Feedback",
        description: "Detailed explanations for every answer with practical insights"
      },
      previewPower: {
        title: "Real Certification Simulation",
        description: "Get a taste of the full certification experience"
      }
    },
    comparison: {
      demo: {
        title: "FREE DEMO",
        features: [
          "12 Handpicked Questions",
          "2 Core Topics",
          "15 Minutes",
          "Instant Results"
        ]
      },
      full: {
        title: "FULL VERSION",
        features: [
          "300+ Expert Questions",
          "All 11 Syllabus Chapters",
          "40 Minutes Real Exam",
          "Detailed Analytics",
          "Performance Tracking"
        ],
        button: "Get Full Access"
      }
    },
    community: "Join AI professional community discovering testing secrets",
    trustIndicators: "Based on ISTQB official materials",
    perfectFor: {
      title: "Perfect For",
      items: [
        "QA Engineers transitioning to AI testing",
        "Software testers expanding skillset",
        "IT professionals seeking ISTQB certification",
        "Anyone preparing for AI testing interviews",
        "Teams upskilling in AI quality assurance",
        "IT Professionals boosting their career"
      ]
    }
  },

  // Access Page
  access: {
    title: "Access Full Quiz",
    subtitle: "Enter Your Access Code",
    description: "Get unlimited access to 300+ expert-level AI testing questions and comprehensive exam preparation.",
    form: {
      title: "Enter Access Code",
      placeholder: "Enter your access code here...",
      button: "Unlock Full Quiz",
      submitting: "Verifying..."
    },
    errors: {
      invalid: "Invalid access code. Please check your code and try again.",
      empty: "Please enter an access code.",
      generic: "Something went wrong. Please try again."
    },
    success: {
      title: "Access Granted! 🎉",
      description: "Welcome to your unlimited full ISTQB AI Tester experience!",
      validDays: "Your access is unlimited and permanent",
      continueButton: "Continue to Full Quiz"
    },
    purchase: {
      title: "Don't Have an Access Code?",
      subtitle: "Get Instant Access",
      description: "Purchase the complete ISTQB AI Tester quiz with 300+ questions, detailed explanations, and comprehensive coverage of all syllabus topics.",
      features: [
        "300+ Expert Questions",
        "All 11 Syllabus Chapters", 
        "Detailed Explanations",
        "Performance Analytics",
        "Mobile Optimized",
        "Lifetime Access"
      ],
      button: "Purchase Full Access",
      note: "Instant download after purchase"
    },
    security: {
      note: "Access codes are securely stored and encrypted",
      expiry: "Unlimited access"
    }
  },

  full: {
    title: "AI Tester Education",
    subtitle: "AI Tester Quiz",
    description: "Master all 11 chapters of the ISTQB AI Testing syllabus",
    features: [
      "300+ Expert Questions",
      "11 Full Chapters",
      "40 minutes like real exam"
    ],
    welcome: {
      keyFeatures: {
        title: "Key Features",
        expertQuestions: {
          title: "300+ Questions",
          features: [
            "Every Syllabus Topic",
            "Real Exam Format",
            "Multiple Difficulty Levels"
          ]
        },
        adaptiveSystem: {
          title: "Adaptive System",
          features: [
            "Personalized Learning",
            "Smart Question Selection",
            "Progress Tracking"
          ]
        },
        detailedAnalytics: {
          title: "Detailed Analytics",
          features: [
            "Performance Insights",
            "Weakness Identification",
            "Study Recommendations"
          ]
        }
      },
      whatMakesDifferent: {
        title: "What Makes This Different?",
        expertCreated: {
          title: "Expert Created",
          description: "Questions created by AI testing professionals who understand both theory and practice"
        },
        completeCoverage: {
          title: "Complete Coverage",
          description: "All 11 chapters of ISTQB AI Tester v1.0 syllabus thoroughly covered"
        },
        realScenarios: {
          title: "Real Scenarios",
          description: "Practical situations you'll encounter in your actual AI testing career"
        },
        trackProgress: {
          title: "Track Progress",
          description: "Detailed analytics help you identify and improve weak areas"
        }
      },
      perfectFor: {
        title: "Perfect For",
        items: [
          "QA Engineers transitioning to AI testing",
          "Software testers expanding skillset",
          "IT professionals seeking ISTQB certification",
          "Anyone preparing for AI testing interviews",
          "Teams upskilling in AI quality assurance",
          "IT Professionals boosting their career"
        ]
      },
      startButton: "Start Your Certification Journey",
      trustIndicators: [
        "Trusted by certification candidates worldwide"
      ]
    }
  },

  quiz: {
    header: {
      title: "AI Tester Quiz",
      timeLeft: "Time Left",
      progress: "Progress",
      streak: "Streak"
    },
    navigation: {
      previous: "Prev",
      next: "Next",
      finish: "Finish",
      questionOverview: "Question Overview",
      swipeHint: "👈 Swipe left/right to navigate 👉"
    },
    status: {
      answered: "Answered",
      notAnswered: "Not answered",
      current: "Current"
    }
  },

  results: {
    title: "DEMO COMPLETE",
    congratulations: "Congratulations!",
    percentage: "% Score",
    complete: "QUIZ COMPLETE",
    status: {
      passed: "Result",
      failed: "Result",
      retry: "RETRY",
      pass: "Pass",
      study: "Not Pass"
    },
    performance: {
      genius: "AI Testing Genius",
      expert: "AI Testing Expert", 
      pro: "AI Testing Pro",
      learner: "AI Testing Learner",
      beginner: "AI Testing Beginner",
      // Full quiz performance levels
      master: "AI Testing Master",
      professional: "AI Testing Professional",
      candidate: "AI Testing Candidate",
      // Perfect score level
      perfectMaster: "🎊 PERFECT MASTER 🎊"
    },
    statistics: {
      correct: "Correct",
      total: "Questions",
      bestStreak: "Best Streak",
      timeSpent: "Status",
      questions: "Questions",
      status: "Status"
    },
    actions: {
      showReview: "Show Answer Review",
      hideReview: "Hide Answer Review",
      tryAgain: "Try Demo Again",
      tryAgainNew: "Try Again with New Questions",
      shareResults: "Share Your Demo Success!",
      shareLinkedIn: "Share on LinkedIn"
    },
    sharing: {
      title: "Share Your Achievement! 🎉",
      description: "Show your network your AI testing knowledge!",
      hashtags: ["ISTQB", "AITesting", "Certification", "TechSkills", "QualityAssurance"],
      messages: {
        success: "🎉 Great! We've opened LinkedIn for you and copied your result to clipboard!",
        clipboard: "🎉 Result copied to clipboard!",
        manual: "Copy this text to share on LinkedIn:"
      },
      // Perfect score sharing
      perfectScore: {
        title: "🎊 PERFECT SCORE ACHIEVED! 🎊",
        description: "Includes secret hashtag for 30% course discount!",
        hashtag: "#ainastasiaquizwinner",
        discountOffer: "30% discount on the full course!",
        championText: "You're now officially an AI Testing Champion!",
        hiddenModeUnlocked: "🎉 INCREDIBLE! YOU'VE UNLOCKED HIDDEN MODE! 🎉",
        hiddenModeTitle: "HIDDEN MODE UNLOCKED!",
        russianCongratulations: "🎉 INCREDIBLE! YOU'VE UNLOCKED HIDDEN MODE! 🎉",
        perfectScoreMessage: "You answered ALL 40 questions correctly and unlocked the secret product mode!",
        championOfferTitle: "🎁 SPECIAL CHAMPION OFFER:",
        specialOffer: "SPECIAL OFFER: Share this post and contact me on any social media with your post link to get 30% discount on the full course!",
        contactInstructions: "Share your LinkedIn post with hashtag #ainastasiaquizwinner and contact me on any social media with your post link - get 30% discount on the main course!",
        linkedinButtonHint: "💡 The 'Share to LinkedIn' button below already contains special text with the secret hashtag"
      }
    },
    upgrade: {
      title: "Ready for the Full Experience?",
      description: "Take your AI testing knowledge to the professional level",
      features: [
        "300+ Expert Questions",
        "11 Full Chapters", 
        "training with unique questions"
      ],
      button: "Get Full Version"
    },
    course: {
      title: "AI Testing Theory Course",
      subtitle: "📚 LEVEL UP YOUR SKILLS",
      description: "Ready to dive deeper? Join our comprehensive theory-based course designed to prepare you for ISTQB AI Tester certification and master AI system testing fundamentals.",
      features: [],
      button: "Get Early Bird Discount",
      // Full quiz course promotion
      fullCourse: {
        nextLevel: "🎓 NEXT LEVEL AWAITS 🎓",
        masterTitle: "AI Testing Theory Course",
        masterDescription: "Take your AI testing knowledge to the professional level with our comprehensive theory course. Learn not just what to test, but HOW to test AI systems like a pro!",
        bonusTitle: "🎁 QUIZ PARTICIPANT BONUS 🎁",
        bonusDescription: "15% OFF + Exclusive Study Materials",
        claimButton: "🔥 CLAIM YOUR BONUS NOW 🔥"
      }
    },
    feedback: {
      title: "Got ideas to improve the product?",
      button: "Leave Feedback"
    },
    social: {
      title: "Follow AI.Nastasia for More AI Testing Insights! 🚀",
      description: "Join the community and stay updated with the latest AI testing trends and tips",
      platforms: {
        instagram: "Instagram",
        tiktok: "TikTok", 
        linkedin: "LinkedIn",
        telegram: "Telegram"
      }
    }
  },

  review: {
    title: "Answer Review",
    yourAnswer: "Your answer:",
    correctAnswer: "Correct answer:",
    explanation: "Explanation:",
    notAnswered: "Not answered"
  },

  languageSelector: {
    title: "Select Language",
    current: "Current Language",
    available: "Available Languages",
    select: "Select"
  },

  mobile: {
    pullToRefresh: "↓ Pull down to refresh ↓",
    swipeInstructions: "👈 Swipe left/right to navigate 👉",
    tapToSelect: "Tap to select an answer",
    doubleTapToConfirm: "Double-tap to confirm"
  },

  a11y: {
    languageSelector: "Language selector",
    questionProgress: "Question {current} of {total}",
    optionSelected: "Option {option} selected",
    timerWarning: "Time running out",
    resultsSummary: "Quiz completed with {score}% score"
  }
}; 